<template>
  <div class="container serp">
    <div class="serp__title-container">
      <div class="serp__title-left">
        <div class="serp__header-button-wrapper"
             v-if="isDataFetched">
          <button
            @click.stop="cancelSortingAndFilters"
            class="serp__clear-filters">
            <span>clear all filters and sorting</span>
            <span class="serp__cross">&times;</span>
          </button>
        </div>

      </div>
      <div class="serp__title-right"
           v-if="isDataFetched">
        <div class="serp__serps">
          <span class="serp__count">Count: {{totalItems}}</span>
          <base-select
            class="serp__grid-select"
            :options="paginationOptions"
            alignLeft
            :titleStyle="{'padding-left': '5px'}"
            :headerStyle="{}"
            showTopBorder
            shiftLabelRight
            v-model="pageItemsLimit"
            :selectStyle="{'max-height': '35px !important', width: '100px !important'}"
            @change="handleLimitChange"
          />
        </div>
        <button class="serp__button" @click="downloadSerp">
          Download
        </button>
      </div>
    </div>

    <template v-if="isDataFetched">
      <GChart
        v-if="isDataFetched"
        :settings="{packages: ['corechart']}"
        :version="'42'"
        :createChart="createChart"
        class="serp__scatter-chart"
      />
    </template>

    <div class="common-table serp__table">
      <div class="common-table__row common-table__header"
           v-if="isDataFetched">
        <div class="common-table__header-cell"
             v-for="column in serpTableColumnsConfig"
             :key="column.property"
             :class="[`serp__${column.property}`]">
          <base-multi-select-sortable-header
            class="serp__select-header"
            :column="column"
            :sort-config="sortConfig"
            :predefinedOptions="filterParams[column.property]"
            :predefinedDateRangeFilter="['timestamp_created', 'query_time' ].includes(column.property) ? {start: filterParams[column.property]['date_from'], end: filterParams[column.property]['date_to']}: null"
            :predefinedNumberFilter="['indexed_pages'].includes(column.property) ? filterParams[column.property] : null"
            @sort="onHeaderCellClicked(column, $event)"
            @apply="filterColumn($event, column)"
            @cancel="cancelFilter($event, column)"
            :ref="sortableHeaderRefs[column.property]"
          />
        </div>
      </div>
      <template v-if="dataMissing">
        <div class="common-table__row no-data-block">
          No records found
        </div>
      </template>
      <div class="" v-else-if="!isDataFetched">
        <div class="serp__loader">
          <base-loader/>
        </div>
      </div>
      <template v-else>
        <template v-for="serp in paginatedSerps">
          <div :key="serp.id" class="common-table__row">
            <div class="common-table__cell"
                 v-for="column in serpTableColumnsConfig"
                 :key="column.property"
                 :class="[`serp__${column.property}`, `serp__row_${column.property}`]">
              <template>
                  <span>
                      {{ serp[column.property] }}
                  </span>
              </template>
            </div>
          </div>
        </template>
      </template>
    </div>
    <div class="pagination-wrapper" v-show="hasPagination">
      <base-pagination :limit="pageItemsLimit"
                       :total-items="totalItems"
                       @pageChanged="pageChanged"/>
    </div>
    <div class="serp__update-loader" v-if="isDataUpdating">
      <base-loader class="serp__update-loader-ring"/>
    </div>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import helper from '@/services/Helper'
import { GChart } from 'vue-google-charts'
import baseModalProps from '@/common/BaseModalProps'
import _ from 'lodash'
import BaseSortMixins from '@/common/BaseSortMixins'
import moment from 'moment'

const ITEMS_PER_PAGE = 10
const EMPTY_FILTERS = {
  timestamp_created: {
    date_from: null,
    date_to: null
  },
  query: '',
  indexed_pages: {
    operator: null,
    value: null
  },
  query_time: {
    date_from: null,
    date_to: null
  },
  location: [],
  error: '',
  sort_column: [],
  sort_order: [],
  page_from: 0,
  page_to: ITEMS_PER_PAGE
}

// table config
const DEFAULT_CONFIG = [
  {
    label: 'Timestamp Created',
    property: 'timestamp_created',
    isSmartInput: true
  },
  {
    label: 'Query',
    property: 'query',
    isSmartInput: true
  },
  {
    label: 'Indexed Pages',
    property: 'indexed_pages',
    isNumber: true
  },
  {
    label: 'Query Time',
    property: 'query_time',
    isSmartInput: false
  },
  {
    label: 'Location',
    property: 'location',
    options: []
  },
  {
    label: 'Error',
    property: 'error'
  }
]
const PAGINATION_OPTIONS = [
  {
    label: '10',
    value: 10
  },
  {
    label: '25',
    value: 25
  },
  {
    label: '50',
    value: 50
  }
]

export default {
  name: 'ViewHistory',
  mixins: [BaseSortMixins],
  components: { GChart },
  props: ['id'],
  data () {
    return {
      isDataFetched: false,
      selectedSerp: null,
      chart: null,
      google: null,
      el: null,
      isDataUpdating: false,
      filteredSerps: [],
      sortConfig: [],
      pageItemsLimit: ITEMS_PER_PAGE,
      totalItems: 0,
      serpTableColumnsConfig: _.cloneDeep(DEFAULT_CONFIG),
      filterParams: _.cloneDeep(EMPTY_FILTERS),
      sortableHeaderRefs: {
        'timestamp_created': 'timestamp_created',
        'query': 'query',
        'indexed_pages': 'indexed_pages',
        'query_time': 'query_time',
        'location': 'location',
        'error': 'error'
      },
      paginationOptions: _.cloneDeep(PAGINATION_OPTIONS)
    }
  },
  computed: {
    ...mapState('serpViewHistory', ['serps', 'settings']),
    chartData () {
      return _.cloneDeep(this.serps).sort((a, b) => {
        const snapshotDate1 = moment(a.timestamp_created).toDate()
        const snapshotDate2 = moment(b.timestamp_created).toDate()
        if (snapshotDate1 < snapshotDate2) {
          return -1
        }
        if (snapshotDate1 > snapshotDate2) {
          return 1
        }
        return 0
      })
    },
    dataMissing () {
      return this.isDataFetched && !this.serps.length
    },
    changeSerpModalProps () {
      const changeSerpModalProps = {
        width: 690
      }
      return { ...baseModalProps, ...changeSerpModalProps }
    },
    hasPagination () {
      return this.isDataFetched && !this.isDataUpdating && this.totalItems > this.pageItemsLimit && !this.dataMissing
    },
    paginatedSerps () {
      let serps = _.cloneDeep(this.serps)
      // eslint-disable-next-line camelcase
      const { page_from = 0, page_to = 100 } = this.filterParams
      // eslint-disable-next-line vue/no-side-effects-in-computed-properties
      this.totalItems = serps.length
      // eslint-disable-next-line vue/no-side-effects-in-computed-properties
      this.filteredSerps = serps
      return serps.slice(page_from, page_to)
    }
  },
  watch: {
    clientId: {
      handler (v) {
        v && this.loadData()
      },
      immediate: true
    },
    settings: {
      deep: true,
      handler (settings) {
        const locations = settings.locations.map(helper.convertListToOptions)
        this.serpTableColumnsConfig.find(item => item.property === 'location').options = locations
      }
    },
    chartData: {
      handler () {
        if (this.el && this.google) this.createChart(this.el, this.google)
      },
      deep: true
    }
  },
  methods: {
    ...mapActions('serpViewHistory', ['getSerpData', 'getSerpSettings']),
    async loadData () {
      this.isDataFetched && (this.isDataUpdating = true)
      await Promise.all([
        this.getSerpData({
          serpId: this.id,
          filters: this.filterParams
        }),
        this.getSerpSettings({ serpId: this.id })
      ])
      this.isDataFetched = true
      this.isDataUpdating = false
    },
    pageChanged ({ from, to }) {
      this.filterParams.page_from = from
      this.filterParams.page_to = to
    },
    /**
     * Callback for createChart property of vue-google-charts library
     * Consider isStacked property in options object manages the way of rendering the chart
     * {@see https://developers.google.com/chart/interactive/docs/gallery/scatterchart#data-format}
     * all list of available options {@see https://developers.google.com/chart/interactive/docs/gallery/scatterchart#data-format}
     * prepared data passed into chat using DataView {@link https://developers.google.com/chart/interactive/docs/datatables_dataviews}
     * @param el - chart html element
     * @param google - link to the Google Chart library
     */
    createChart (el, google) {
      if (!google || !el || !this.chartData || !this.chartData.length) {
        return
      }

      this.google = google
      this.el = el

      const dataTable = new google.visualization.DataTable()

      dataTable.addColumn('date', 'Snapshot Date')
      dataTable.addColumn('number', 'Indexed Pages')

      const rows = []
      const xTicks = []
      this.chartData.forEach((snapshot, index) => {
        const date = moment(snapshot.timestamp_created).toDate()

        const mappedSnapshot = []
        mappedSnapshot.push(date)
        mappedSnapshot.push(snapshot.indexed_pages ? parseInt(snapshot.indexed_pages) : 0)
        rows.push(mappedSnapshot)

        if (index === 0 || index === this.chartData.length - 1) {
          xTicks.push({ v: date, f: date })
        } else {
          xTicks.push({ v: date, f: '' })
        }
      })
      dataTable.addRows(rows)

      const options = {
        backgroundColor: 'transparent',
        height: 400,
        width: '100%',
        chartArea: { width: '92%', height: '80%', left: '5%', top: 10 },
        legend: { position: 'none' },
        fontName: 'Roboto',
        tooltip: {
          isHtml: true
        },
        hAxis: {
          ticks: [...xTicks],
          format: 'y-M-d',
          textStyle: {
            color: '#b2b9be',
            fontSize: 10
          }
        },
        pointSize: 7,
        focusTarget: 'category',
        vAxis: {
          baselineColor: 'transparent',
          textStyle: {
            color: '#b2b9be',
            fontSize: 12,
            fontWeight: 500,
            bold: true
          }
        },
        series: [
          { lineWidth: 2 }
        ],
        fontSize: 12,
        selectionMode: 'multiple',
        title: 'none'
      }

      const chart = new google.visualization.ScatterChart(el)
      chart.draw(dataTable, options)
      this.chart = chart
    },
    handleLimitChange (value) {
      this.pageItemsLimit = value
      this.filterParams = { ...this.filterParams, page_from: 0, page_to: value }
    },
    filterColumn (event, column) {
      this.filterParams[column.property] = event
      this.filterParams.page_from = 0
      this.filterParams.page_to = this.pageItemsLimit
      this.loadData()
    },
    onHeaderCellClicked (cell, direction, declineCall) {
      if (direction === 'clear') {
        this.sortConfig.shift()
        return
      }
      if (this.sortConfig.length) {
        this.sortConfig[0].propertyName = cell.property
        this.sortConfig[0].sortDirection = direction
      } else if (cell) {
        this.sortConfig = [{
          sortDirection: direction,
          propertyName: cell.property
        }]
      }
      this.filterParams = {
        ...this.filterParams,
        sort_column: this.sortConfig.map(item => item.propertyName),
        sort_order: this.sortConfig.map(item => item.sortDirection)
      }
      !declineCall && this.loadData()
    },
    cancelSortingAndFilters () {
      const emptyFilters = _.cloneDeep(EMPTY_FILTERS)
      this.filterParams = { ...this.filterParams, ...emptyFilters, page_to: this.pageItemsLimit }
      Object.keys(this.sortableHeaderRefs)
        .forEach(ref => {
          const [element] = this.$refs[ref]
          element.$refs[element.childRef].cancel(true)
        })
      this.sortConfig = []
      !this.isDataUpdating && this.loadData()
    },
    cancelFilter (event, column) {
      this.filterParams[column.property] = null
      this.loadData()
    },
    downloadSerp () {
      window.location.href = `/serp_tracker/download_report?id=${this.id}`
    }
  }
}
</script>

<style scoped lang="scss">

.common-table {
  border-radius: 4px;
  overflow: visible;

  &__header {
    background-color: rgba(1, 22, 39, 0.1);
    border-top-right-radius: 4px;
    border-top-left-radius: 4px;
  }

  &__header-cell {
    color: $rfx-dark-march;
    font-weight: bold;
    padding: 0;
    height: 60px;
    display: flex;
    align-items: center;
    cursor: pointer;
  }

  &__cell {
    color: $rfx-dark-march;
    height: 60px;
    padding: 0;
    display: flex;
    align-items: center;
    font-size: 14px;
    border-bottom: 1px solid $rfx-light-steel;

    &:first-child {
      padding-left: 25px;
    }

    &:last-child {
      padding-right: 25px;
    }
  }

  &__row {
    border-left: 1px solid $rfx-light-steel;
    border-right: 1px solid $rfx-light-steel;
  }
}

.serp {
  position: relative;
  min-height: 200vh !important;

  &__cross {
    font-size: 21px;
    margin-left: 5px;
    margin-top: -2px;
  }

  &__title-right {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    flex: 1 1 50%;
    height: 60px;
  }

  &__title-right > *:not(:last-child) {
    margin-right: 30px;
  }

  &__title-left {
    display: flex;
    align-items: center;
  }

  &__title-left > *:not(:last-child) {
    margin-right: 30px;
  }

  &__header-button-wrapper {
    border-radius: 15px;
    border: solid 1px $rfx-light-steel;
    background: white;
    height: 28px;
    display: flex;
    padding: 0 10px;
  }

  &__copy-button {
    flex: 1 1 50%;
    height: 100%;
    border: none;
    outline: none;
    color: $rfx-dark-march;
    white-space: nowrap;
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 90px;
    background-color: #fff;
    font-size: 11px;

    &:hover {
      opacity: 0.5;
    }
  }

  &__clear-filters {
    @extend .serp__copy-button;
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: auto;

    span:first-child {
      font-size: 11px;
    }
  }

  &::v-deep &__tooltip {
    padding: 0 5px;
  }

  &__grid-select {
    flex-basis: 100px;
  }

  &__title-container {
    padding: 14px 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  &__loader {
    display: flex;
    width: 100%;
    justify-content: center;
    align-items: center;
    height: calc(100vh - 530px);
  }

  &__table {
    position: relative;
    display: flex;
    flex-direction: column;
  }

  &__update-loader {
    position: absolute;
    display: flex;
    width: 100%;
    height: 100%;
    justify-content: center;
    background-color: white;
    top: 0;
    opacity: 0.5;
    z-index: 10000;
  }

  &__update-loader-ring {
    margin-top: 200px;
  }

  &__serps {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-between;
    height: 100%;
  }

  &__timestamp_created {

    &.common-table__header-cell {
      flex-basis: 0.7%;

      &::v-deep .multi-select__title-container {
        padding-left: 25px;
      }
    }
  }

  &__error {

    &.common-table__header-cell {
      flex-basis: 2.6%;
    }
  }

  &__url {
    flex: 1 0 120px;
    color: $rfx-dark-march;
  }

  &__target_date,
  &__url_serp,
  &__brief_serp,
  &__page_types,
  &__keywords,
  &__msv,
  &__section {
    flex: 0 1 150px;
  }

  &__edit-button {
    flex: 0 0 45px;
  }

  &__integrations {
    position: relative;
    flex: 0 1 25px;

    img {
      width: 16px;
    }
  }

  &__url_serp {
    flex-basis: 145px;
    color: $rfx-dark-march;
    font-weight: 500;
  }


  &__button {
    width: 136px;
    padding: 0 20px;
    height: 60px;
    border-radius: 4px;
    font-size: 10px;
    font-weight: 900;
    text-transform: uppercase;
    border: none;
    outline: none;
    cursor: pointer;
    background-color: $rfx-medium-march;
    color: #fff;

    &__asana {
      margin: 15px 0;
    }
  }

  &__count {
    color: #b2b9be;
    font-size: 14px;
    font-weight: 600;
    text-decoration: none;
    width: 75px;
    white-space: nowrap;
  }
}

.pagination-wrapper {
  display: flex;
  height: 60px;
  margin-top: 10px;
  justify-content: center;
  align-items: center;
  border: 1px solid $rfx-light-steel;
  border-radius: 4px;
}

.no-data-block {
  border-bottom: 1px solid $rfx-light-steel;
}

.common-table {
  &__header-cell {
    color: $rfx-dark-march;
    font-weight: bold;
  }
}

.edit-icon {
  cursor: pointer;
  font-size: 20px;
  justify-self: end;
  display: grid;
  color: $rfx-dark-march;
}

</style>
